import React, {useState, useCallback, useRef, useEffect, useMemo} from 'react';
import {useTranslation} from 'react-i18next';

import {Layout} from '@phonectas/unifon-ui-kit';
import {DropdownWrapper, StyledButtonGroup, StyledDropdownUl, StyledNavLi, StyledNavUl} from './styled';
import NewShevronDown from '../icons/NewShevronDown';
import NewShevronUp from '../icons/NewShevronUp';
import NewCalenderIcon from '../icons/NewCalenderIcon';
import BtnSmsIcon from '../icons/BtnSmsIcon';
import BtnEmailIcon from '../icons/BtnEmailIcon';
import NewDownloadIcon from '../icons/NewDownloadIcon';

interface VismaActionBtnsProps {
	sendSms: () => void;
	invoiceCopy: () => void;
	postPoneInvoice: () => void;
	downloadInvoice: (type: string) => void;
	disabled?: boolean;
}

const VismaActionBtns: React.FC<VismaActionBtnsProps> = ({
	sendSms,
	invoiceCopy,
	postPoneInvoice,
	downloadInvoice,
	disabled,
}) => {
	const [t] = useTranslation();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const clickInside = useRef(false);
  
	const onPostponeInvoice = useCallback(() => {
		  postPoneInvoice();
	}, [postPoneInvoice]);
  
	const onSendInvoiceCopyByEmail = useCallback(() => {
		  invoiceCopy();
	}, [invoiceCopy]);
  
	const onSendInvoiceDetailsBySMS = useCallback(() => {
		  sendSms();
	}, [sendSms]);
  
	const onDownloadOriginalInvoice = useCallback(() => {
		  downloadInvoice('download-invoice');
	}, [downloadInvoice]);
  
	const onDownloadLatestInvoice = useCallback(() => {
		  downloadInvoice('download-latest');
	}, [downloadInvoice]);
  
	const toggleDropdown = useCallback(() => {
		setIsDropdownOpen((prev) => !prev);
	}, []);
  
	const dropdownItems = useMemo(() => [
		{label: t('Original invoice'), onClick: onDownloadOriginalInvoice},
		{label: t('Latest invoice'), onClick: onDownloadLatestInvoice},
	], [t, onDownloadOriginalInvoice, onDownloadLatestInvoice]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (clickInside.current) {
				clickInside.current = false;
				return;
			}
  
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsDropdownOpen(false);
			}
		};
  
		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);
  
	return (
		<Layout direction="row" alignItems="center" fullWidth nowrap>
			<StyledNavUl>
				<li>
					<StyledButtonGroup
						icon={
							<span style={{marginRight: '10px', display: 'flex', alignItems: 'center'}}>
								<NewCalenderIcon />
							</span>
						}
						onClick={onPostponeInvoice}
						disabled={disabled || false}
					>
						{t('Postpone invoice')}
					</StyledButtonGroup>
				</li>
				<li>
					<StyledButtonGroup
						icon={
							<span style={{marginRight: '10px', display: 'flex', alignItems: 'center'}}>
								<BtnEmailIcon />
							</span>
						}
						onClick={onSendInvoiceCopyByEmail}
					>
						{t('Send invoice copy')}
					</StyledButtonGroup>
				</li>
				<li>
					<StyledButtonGroup
						icon={
							<span style={{marginRight: '10px', display: 'flex', alignItems: 'center'}}>
								<BtnSmsIcon />
							</span>
						}
						onClick={onSendInvoiceDetailsBySMS}
					>
						{t('Send details by SMS')}
					</StyledButtonGroup>
				</li>
				<li>
					<StyledButtonGroup
						icon={
							<span style={{marginRight: '10px', display: 'flex', alignItems: 'center'}}>
								<NewDownloadIcon />
							</span>
						}
						onClick={(e) => {
							e.preventDefault();
				        clickInside.current = true;
							toggleDropdown();
						}}
					>
						{t('Download invoice')}
						<span style={{transition: 'all 0.3s ease', marginLeft: '5px'}}>
							{isDropdownOpen ? <NewShevronUp /> : <NewShevronDown />}
						</span>
					</StyledButtonGroup>
					{isDropdownOpen && (
						<DropdownWrapper ref={dropdownRef}>
							<StyledDropdownUl>
								{dropdownItems.map((item, index) => (
									<StyledNavLi
										key={index}
										onClick={(e) => {
											e.preventDefault();
											item.onClick();
											setIsDropdownOpen(false);
										}}
									>
										{item.label}
									</StyledNavLi>
								))}
							</StyledDropdownUl>
						</DropdownWrapper>
					)}
				</li>
			</StyledNavUl>
		</Layout>
	);
};
  
export default VismaActionBtns;